// Pure helpers for the Deadlines tab — the due-date ranges that route a bucket
// into the Work tab. No axios, no component, mirroring hades_filters.js /
// hades_bulk.js's separation so the date math is testable without mounting
// Support_Hades.
//
// EVERY DATE HERE IS A BARE CALENDAR DAY, NOT AN INSTANT. week_start and every
// due date in GET /api/requirements/deadlines/ are YYYY-MM-DD strings from a
// Django DateField. Adding days with plain Date math and reading the result
// back through toISOString() would land on the wrong day for anyone west of
// Greenwich — this exact bug has shipped twice already in this module (#2053,
// #2237). Every function below goes through toLocalDate() on the way in and
// formats the local calendar fields back out; nothing here touches
// toISOString() or a UTC accessor.

import { toLocalDate } from '../support_constants';

const pad2 = (n) => String(n).padStart(2, '0');

function formatLocalDate(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

// dateStr + delta LOCAL calendar days. New Date(y, m, d + delta) normalises
// month/day overflow itself, so this holds across month and year boundaries
// with no special-casing, and it operates on wall-clock fields rather than
// elapsed milliseconds so a DST transition inside the range cannot shift the
// result by an hour into the wrong day.
export function addLocalDays(dateStr, delta) {
  const d = toLocalDate(dateStr);
  if (!d) return null;
  return formatLocalDate(new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta));
}

// Today as a bare local calendar day — never toISOString(), which reads back
// as yesterday's date for anyone east of UTC between local midnight and the
// UTC rollover.
export function localTodayStr() {
  return formatLocalDate(new Date());
}

// The Monday-start week's last day. Does not read the payload's
// week_starts_on — every week in this payload is Monday-start by
// construction (hades_views._ticket_flow uses the same Postgres
// date_trunc('week', ...) so the two weekly series on this board cannot
// disagree about when a week starts), and there is nowhere else for a
// different value to point yet.
export function weekEndDate(weekStart) {
  return addLocalDays(weekStart, 6);
}

// A $router.push target into the Work tab, due-date-bounded to [from, to].
// Either bound may be null for an open end (Overdue has no lower bound;
// Beyond has no upper one). Param names verified against
// Support_List.parseQueryToFilters, NOT assumed from this board's OWN Due
// filter — hades_filters.js HADES_FILTERS uses the shorter due_from/due_to
// pair for the requirement_due_date filter on THIS board, a different
// grammar reading a different query string on the same page.
//
// `extra` carries whichever breakdown query param the caller is linking
// through — {owner: id} for the by-engineer matrix, {tenant: id | '__none__'}
// for the by-tenant one — merged in as-is. This function does not know or
// care which; Hades_BreakdownMatrix.vue decides the key and the null-id
// substitution (or lack of one) per breakdown, since owner and tenant differ
// there.
//
// BOTH FILTERS ACCEPT A '__none__' SENTINEL — Support_FilterPopover.vue
// offers "No owner" the same way Support_List offers "No Tenant", and
// Support_List._tabFilterResult honours '__none__' for owner exactly like it
// does for tenant. The asymmetry is one step later: parseQueryToFilters puts
// `owner` in numericFields, so `?owner=__none__` runs through parseNum,
// becomes NaN, and is dropped by the numericFields filter — the sentinel
// never survives the URL round-trip. `tenant` has its own parseTenant branch
// (`v === '__none__' ? v : parseNum(v)`) that preserves it. So a null
// owner_id (Unowned) still cannot be linked from here — the link would land
// on an unfiltered list — while a null tenant_id (Platform) can, via
// '__none__'. One line in parseQueryToFilters would fix the owner side too;
// tracked in kaironlibrary hades/BUGS_AND_TECH_DEBT.txt, not done here.
export function workLink(from, to, extra = {}) {
  const query = { ...extra };
  if (from) query.due_date_from = from;
  if (to) query.due_date_to = to;
  return { path: '/support/work', query };
}

export function weekWorkLink(weekStart, extra) {
  return workLink(weekStart, weekEndDate(weekStart), extra);
}

// due_date < current_date is the backend's overdue predicate (hades_views'
// bucket CASE), which excludes today — so the upper bound here is yesterday,
// not today, and there is no lower bound: "overdue" reaches arbitrarily far
// into the past.
export function overdueWorkLink(extra) {
  return workLink(null, addLocalDays(localTodayStr(), -1), extra);
}

// One day past the last zero-filled week's end. `weeks` must be the payload's
// own array — already sorted ascending server-side — so this never
// re-derives the horizon boundary from horizon_weeks and today's date
// independently of what the server actually zero-filled.
export function beyondWorkLink(weeks, extra) {
  if (!weeks || !weeks.length) return workLink(null, null, extra);
  const last = weeks[weeks.length - 1];
  return workLink(addLocalDays(last.week_start, 7), null, extra);
}

// NO DATE BOUND AT ALL, deliberately — this is the Total column's link (both
// matrices), and Total sums Overdue + every week + Beyond + Undated. Undated
// has no due_date operator that isolates it (see the note at the bottom of
// this file), so the only query that reproduces "everything in Total" is one
// with no due_date filter whatsoever, not a wide open/close pair that would
// still exclude undated rows.
export function totalWorkLink(extra) {
  return workLink(null, null, extra);
}

// UNDATED HAS NO WORK LINK, ON PURPOSE. Support_List.parseQueryToFilters'
// date kind only has on_or_after / on_or_before scalar operators — there is
// no "is unset" operator in that grammar — so there is no due_date query that
// isolates rows with no due date at all. Building a link here would either
// silently show every row (no filter) or require adding an operator to a
// different module's filter grammar, which is out of scope for this tab.
